import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TranslateIcon from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import { getLanguageOption, SUPPORTED_LANGUAGES } from "../../i18n/languages";

export default function LanguageSwitcher({ size = "small" }) {
  const { i18n, t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const current = useMemo(
    () => getLanguageOption(i18n.resolvedLanguage || i18n.language),
    [i18n.language, i18n.resolvedLanguage],
  );

  const currentLanguageLabel = t(`common.language.${current.code}`);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (newLanguage) => {
    if (!newLanguage || newLanguage === current.code) {
      handleClose();
      return;
    }
    i18n.changeLanguage(newLanguage);
    handleClose();
  };

  return (
    <>
      <Tooltip
        title={t("layout.language.tooltip", {
          language: `${currentLanguageLabel} (${current.code.toUpperCase()})`,
        })}
      >
        <Button
          size={size}
          variant="outlined"
          onClick={handleOpen}
          aria-label={t("layout.language.ariaLabel")}
          aria-haspopup="menu"
          aria-expanded={open ? "true" : undefined}
          endIcon={<KeyboardArrowDownIcon />}
          startIcon={<TranslateIcon fontSize="small" />}
          sx={{
            px: 1.25,
            minWidth: 64,
            fontWeight: 700,
            textTransform: "none",
            borderColor: "divider",
          }}
        >
          <Box component="span" sx={{ mr: 0.75, lineHeight: 1 }}>
            {current.flag}
          </Box>
          {current.code.toUpperCase()}
        </Button>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{
          "aria-label": t("layout.language.ariaLabel"),
        }}
      >
        {SUPPORTED_LANGUAGES.map((option) => {
          const optionLanguage = t(`common.language.${option.code}`);
          const isActive = option.code === current.code;

          return (
            <MenuItem
              key={option.code}
              selected={isActive}
              onClick={() => handleChange(option.code)}
              aria-label={`${optionLanguage} (${option.code.toUpperCase()})`}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                <Box component="span" sx={{ fontSize: "1rem", lineHeight: 1 }}>
                  {option.flag}
                </Box>
              </ListItemIcon>
              <ListItemText
                primary={optionLanguage}
                secondary={option.code.toUpperCase()}
              />
              {isActive ? <CheckIcon fontSize="small" color="primary" /> : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
